import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  chargeInvoiceWithSavedBank,
  chargeInvoiceWithSavedCard,
  confirmInvoiceAutoDraft,
  confirmInvoiceBankInvoice,
  confirmInvoiceCardPayment,
  confirmInvoiceMailPayment,
  createCardPaymentIntent,
  createInvoicePaymentIntent,
  getInvoiceForPayment,
  getStripe,
  setupInvoiceAutoDraft,
  submitInvoiceAchPayment,
} from '../lib/stripe.js'
import logo from '../assets/yesnet-logo.png'

// Public payment page at /pay?invoice=<id>. No auth, no admin chrome.
//
// Customer enters bank details directly — account-holder name, routing
// number, account number (with confirmation field). Server tokenizes via
// Stripe and submits the ACH PaymentIntent. The submission screen
// confirms the charge is in flight; the webhook flips the invoice to
// 'paid' and sends the receipt once Stripe finishes verification +
// ACH settlement (1-3 business days).
//
// Auto-draft customers see a read-only notice and never reach the form.
export default function PayPage() {
  const [params] = useSearchParams()
  const invoiceId = params.get('invoice') || ''
  const [invoice, setInvoice] = useState(undefined) // undefined=loading, null=not found, object=loaded
  const [done, setDone] = useState(false)
  // First-time chooser state. When the customer arrives with no payment
  // method on file, we show the 3-card chooser. The selection is held in
  // local state until they complete setup — only after a successful
  // attach does the customer's `paymentMethod` field get written.
  const [chosenRail, setChosenRail] = useState(null) // 'auto-draft' | 'bank-invoice' | 'card-invoice'

  useEffect(() => {
    if (!invoiceId) { setInvoice(null); return }
    let cancelled = false
    ;(async () => {
      try {
        const r = await getInvoiceForPayment(invoiceId)
        if (!cancelled) setInvoice(r)
      } catch {
        if (!cancelled) setInvoice(null)
      }
    })()
    return () => { cancelled = true }
  }, [invoiceId])

  if (invoice === undefined) return <Shell><Loading /></Shell>
  if (!invoice) return <Shell><NotFound /></Shell>
  // After a successful setup, render the rail-specific success message so
  // the customer sees the right copy about whether they were charged today
  // or just had their payment method saved.
  if (done) {
    return <Shell><PaymentReceived invoice={invoice} rail={done} /></Shell>
  }
  if (invoice.isPaid) return <Shell><AlreadyPaid invoice={invoice} /></Shell>
  // Payment is in flight (webhook flipped invoice to 'processing'). Show
  // the same reassuring confirmation — never a Pay button (no double-
  // charge possible).
  const isInFlight = invoice.status === 'processing'
  if (isInFlight) return <Shell><PaymentReceived invoice={invoice} rail="bank-invoice" /></Shell>

  // First-time customer: no saved payment method on the account yet. Show
  // the 3-card chooser, regardless of what their `paymentMethod` preference
  // field happens to say — until something is actually saved, the choice
  // hasn't been made. After they pick, the local `chosenRail` flows into
  // the appropriate sub-component below.
  //
  // Customers who already have a saved bank or card skip the chooser
  // entirely and land on the one-tap repeat-pay screen for their rail.
  const hasSavedAny = !!invoice.hasSavedBank || !!invoice.hasSavedCard
  if (!hasSavedAny && !chosenRail) {
    return (
      <Shell>
        <FirstTimeChooser invoice={invoice} onPick={setChosenRail} />
      </Shell>
    )
  }

  // Active rail. chosenRail (user's chooser pick) takes priority over
  // invoice.paymentMethod (server default). Otherwise the server's
  // fallback default of 'stripe' for a fresh customer would override an
  // explicit 'auto-draft' / 'bank-invoice' / 'card-invoice' selection —
  // routing the customer to the wrong (charging) component.
  const rail = chosenRail || invoice.paymentMethod

  // stripe_invoice / bank-invoice — existing saved-bank (Bank on File)
  // customers only. Bank on File is no longer offered to NEW customers in the
  // chooser, but the 7 existing saved-bank customers still land here on their
  // repeat invoices: StripeInvoicePay one-tap "Pay $X" against the saved bank.
  if (rail === 'stripe_invoice' || rail === 'bank-invoice') {
    return <Shell><StripeInvoicePay invoice={invoice} invoiceId={invoiceId} onSuccess={(railKey) => setDone(railKey || 'bank-invoice')} /></Shell>
  }
  // Mailing Payment — no Stripe, no PM. The customer mails a check by the
  // due date; we just write paymentMethod:'mail' on confirmation.
  if (rail === 'mail') {
    return <Shell><MailSetup invoice={invoice} invoiceId={invoiceId} onSuccess={() => setDone('mail-set')} /></Shell>
  }
  // card_invoice / card_auto / card-invoice — Stripe Card Element setup-
  // and-charge. Both render the same card form; the difference is what
  // happens on subsequent invoices: card_invoice = one-tap, card_auto =
  // auto-charged by scheduler.
  if (rail === 'card_invoice' || rail === 'card_auto' || rail === 'card-invoice') {
    return <Shell><CardInvoicePay invoice={invoice} invoiceId={invoiceId} onSuccess={() => setDone('card-invoice')} /></Shell>
  }
  // Auto draft (bank-link only, NO charge today) — when the customer picks
  // "Auto Draft" from the chooser. Uses Stripe Financial Connections via a
  // SetupIntent so the bank is saved without taking a payment. The current
  // invoice is marked `pendingFirstCharge` so the daily scheduler skips it
  // and admin runs the first charge manually via Charge Now. Future months
  // auto-draft on the due date.
  if (rail === 'auto-draft') {
    return <Shell><AutoDraftSetup invoice={invoice} invoiceId={invoiceId} onSuccess={() => setDone('auto-draft')} /></Shell>
  }
  if (invoice.isAutoDraft) return <Shell><AutoDraftNotice invoice={invoice} /></Shell>

  return (
    <Shell>
      <PayForm
        invoice={invoice}
        invoiceId={invoiceId}
        onSuccess={() => setDone('bank-invoice')}
      />
    </Shell>
  )
}

// First-time chooser — 3 large cards. Selection sets local state on PayPage
// which flows into the appropriate sub-component below. We do NOT write
// to the customer record here; that happens only after a successful
// payment-method attach so a customer who bails halfway doesn't end up
// with a half-set rail.
function FirstTimeChooser({ invoice, onPick }) {
  const opts = [
    {
      key:  'auto-draft',
      rail: 'auto-draft',
      title: 'Auto Draft',
      blurb: 'Link your bank account. We draft your payment automatically each month. Nothing to do after setup.',
      icon:  '⚡',
    },
    {
      key:  'debit-card',
      rail: 'card-invoice',
      title: 'Debit Card',
      blurb: "Add your debit card. We send you an invoice each month letting you know it's processing, then a receipt once it goes through.",
      icon:  '💳',
    },
    {
      key:  'credit-card',
      rail: 'card-invoice',
      title: 'Credit Card',
      blurb: "Add your credit card. We send you an invoice each month letting you know it's processing, then a receipt once it goes through.",
      icon:  '💳',
    },
  ]
  return (
    <div>
      <h1 className="font-extrabold text-2xl text-center">One-Time Payment Setup</h1>
      <p className="text-sm text-gray-900 text-center mt-2 leading-relaxed">
        Choose how you want to pay your YesNet WiFi bill. This setup only happens once.
      </p>
      <div className="bg-gray-50 rounded-lg p-4 mt-5 text-center border border-gray-200">
        <div className="text-xs uppercase tracking-widest text-gray-900 font-bold">Invoice Amount</div>
        <div className="text-3xl font-extrabold text-brand-green mt-1">{fmtMoney(invoice.amount)}</div>
        <div className="text-sm text-gray-900 mt-1">Due {fmtDate(invoice.dueDate)}</div>
      </div>

      <div className="mt-5 space-y-3">
        {opts.map((o) => (
          <button
            key={o.key}
            type="button"
            onClick={() => onPick(o.rail)}
            className="w-full text-left rounded-xl border-2 border-gray-200 hover:border-brand-green hover:bg-[#f4f7fc] transition px-4 py-4 flex gap-4 items-start active:scale-[0.99]">
            <span className="text-3xl leading-none">{o.icon}</span>
            <span className="flex-1">
              <span className="block font-extrabold text-base text-gray-900">{o.title}</span>
              <span className="block text-sm text-gray-900 mt-1 leading-snug">{o.blurb}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

function fmtDate(d) {
  if (!d) return ''
  const dd = new Date(d + 'T00:00:00')
  if (isNaN(dd.getTime())) return d
  return dd.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

// ── stripe_invoice (Financial Connections) flow ────────────────────────────
// Two states:
//   firstTime (no saved PM): tap Pay → createInvoicePaymentIntent →
//     stripe.collectBankAccountForPayment opens FC modal → user picks
//     bank + logs in → stripe.confirmUsBankAccountPayment finalizes.
//     Webhook saves the PM to the customer so the next invoice is one-tap.
//   repeat (saved PM): tap Pay → chargeInvoiceWithSavedBank → off_session
//     ACH charge against the saved bank.
function StripeInvoicePay({ invoice, invoiceId, onSuccess }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const firstTime = !invoice.hasSavedBank

  const go = async () => {
    setError(''); setBusy(true)
    try {
      if (!firstTime) {
        const r = await chargeInvoiceWithSavedBank(invoiceId)
        if (!r?.ok) throw new Error(r?.error || 'Could not charge saved bank.')
        onSuccess?.('bank-invoice')
        return
      }

      // First-time Bank on File: SetupIntent FC flow — link only, NO charge.
      // Reuses /setupInvoiceAutoDraft which returns a clientSecret for a
      // plain SetupIntent. After collect + confirm succeed, the new
      // /confirmInvoiceBankInvoice endpoint persists the PM with
      // paymentMethod:'stripe_invoice' + autoDraft:false. The customer is
      // expected to come back and click Pay themselves on each invoice.
      const stripe = await getStripe()
      if (!stripe) throw new Error('Payment system unavailable — please refresh and try again.')

      const { clientSecret } = await setupInvoiceAutoDraft(invoiceId)
      if (!clientSecret) throw new Error('Setup could not start — please try again.')

      const collect = await stripe.collectBankAccountForSetup({
        clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: {
              name: invoice.customerName || 'YesNet WiFi Customer',
              email: invoice.customerEmail || undefined,
            },
          },
        },
      })
      if (collect.error) throw new Error(collect.error.message || 'Bank link cancelled.')

      let si = collect.setupIntent
      if (si?.status === 'requires_confirmation') {
        const confirmed = await stripe.confirmUsBankAccountSetup(clientSecret)
        if (confirmed.error) throw new Error(confirmed.error.message || 'Bank link confirmation failed.')
        si = confirmed.setupIntent
      }
      if (si?.status !== 'succeeded') {
        // instant-only setup: a bank that can't be instantly verified ends
        // here (never micro-deposit / requires_action). Fail clean — nothing
        // saved, nothing pending — with a clear, non-technical message.
        throw new Error("This bank can't be verified instantly — please use a card or a different bank.")
      }
      const pmId = typeof si.payment_method === 'string'
        ? si.payment_method
        : si.payment_method?.id
      if (!pmId) throw new Error('Bank link succeeded but no payment method was returned.')

      const r = await confirmInvoiceBankInvoice(invoiceId, pmId)
      if (!r?.ok) throw new Error(r?.error || 'Could not finish bank-on-file setup.')
      onSuccess?.('bank-invoice-linked')
    } catch (e) {
      setError(e?.message || 'Could not complete setup.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="text-center">
      <InvoiceHeader invoice={invoice} />

      <div className="mt-6 mb-4 text-sm text-gray-900">
        {firstTime ? (
          <>
            <p className="mb-2">
              Tap below to securely link your bank account through Stripe.
              You will <b>NOT</b> be charged today.
            </p>
            <p className="text-xs text-gray-900">
              We'll send you an invoice each month — click Pay when ready.
              After this first setup, every invoice can be paid with a single tap.
            </p>
          </>
        ) : (
          <>
            <p className="mb-2">
              Your bank is already linked. Tap below to confirm payment of this invoice.
            </p>
            <p className="text-xs text-gray-900">
              Bank transfers typically clear within 3–5 business days.
            </p>
          </>
        )}
      </div>

      {error ? (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg px-3 py-2 mb-3 text-left">
          {error}
        </div>
      ) : null}

      <button
        type="button"
        onClick={go}
        disabled={busy}
        className="btn-gold w-full"
      >
        {busy
          ? (firstTime ? 'Opening secure bank link…' : 'Submitting payment…')
          : (firstTime ? 'Link Bank for Pay-by-Invoice' : `Pay ${fmtMoney(invoice.amount)}`)}
      </button>
      <p className="text-[11px] text-gray-900 mt-3">
        Powered by Stripe — your bank credentials never touch our servers.
      </p>
    </div>
  )
}

// ── Mailing Payment (no Stripe) ───────────────────────────────────────────
// Customer picked "Mailing Payment" in the chooser. We only confirm the
// choice — no Stripe customer, no SetupIntent, no PM. On confirm we POST to
// /confirmInvoiceMailPayment which writes paymentMethod:'mail' on the
// customer record. The current invoice stays unpaid until admin marks it
// paid when the check arrives.
function MailSetup({ invoice, invoiceId, onSuccess }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const go = async () => {
    setError(''); setBusy(true)
    try {
      const r = await confirmInvoiceMailPayment(invoiceId)
      if (!r?.ok) throw new Error(r?.error || 'Could not save your choice.')
      onSuccess?.()
    } catch (e) {
      setError(e?.message || 'Could not save your choice.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="text-center">
      <h1 className="font-extrabold text-2xl">Mailing Payment</h1>
      <p className="text-sm text-gray-900 mt-2 leading-relaxed">
        Click here only if you have been approved to mail your payment.
      </p>

      <div className="bg-gray-50 rounded-lg p-4 mt-5 border border-gray-200 text-left">
        <div className="text-xs uppercase tracking-widest text-gray-900 font-bold">
          You will NOT be charged today
        </div>
        <p className="text-sm text-gray-900 mt-2 leading-relaxed">
          Confirm below and we'll mark your account as <b>mail-pay</b>. Mail
          your check for invoice <b>#{invoice.invoiceNumber}</b> ({fmtMoney(invoice.amount)})
          to YesNet WiFi by your due date.
        </p>
      </div>

      {error ? (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg px-3 py-2 mt-4 mb-2 text-left">
          {error}
        </div>
      ) : null}

      <button
        type="button"
        onClick={go}
        disabled={busy}
        className="btn-gold w-full mt-5"
      >
        {busy ? 'Saving your choice…' : 'Confirm Mailing Payment'}
      </button>
    </div>
  )
}

// ── Auto Draft (SetupIntent, no charge today) ─────────────────────────────
// Customer picked "Auto Draft" in the chooser. Bank is linked via Stripe
// Financial Connections through a SetupIntent — no PaymentIntent, no
// charge. After the SetupIntent succeeds, the server persists the PM,
// flips the customer onto the auto-draft rail (autoDraft:true +
// paymentMethod:'stripe_invoice'), and marks the current invoice as
// pendingFirstCharge so the scheduler skips it. Admin runs the first
// charge manually via Charge Now; subsequent months auto-draft.
function AutoDraftSetup({ invoice, invoiceId, onSuccess }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const go = async () => {
    setError(''); setBusy(true)
    try {
      console.log('[autodraft] STEP 1 — calling setupInvoiceAutoDraft on server with invoiceId:', invoiceId)
      const stripe = await getStripe()
      if (!stripe) throw new Error('Payment system unavailable — please refresh and try again.')

      const setupResponse = await setupInvoiceAutoDraft(invoiceId)
      console.log('[autodraft] STEP 1 RESULT — server returned:', setupResponse)
      const { clientSecret } = setupResponse
      if (!clientSecret) throw new Error('Setup could not start — please try again.')

      const collectParams = {
        clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: {
              name: invoice.customerName || 'YesNet WiFi Customer',
              email: invoice.customerEmail || undefined,
            },
          },
        },
      }
      console.log('[autodraft] STEP 2 — calling stripe.collectBankAccountForSetup with EXACT params:', JSON.stringify(collectParams, null, 2))

      const collect = await stripe.collectBankAccountForSetup(collectParams)
      console.log('[autodraft] STEP 2 RESULT — collect.error:', collect.error)
      console.log('[autodraft] STEP 2 RESULT — collect.setupIntent.status:', collect.setupIntent?.status)
      console.log('[autodraft] STEP 2 RESULT — collect.setupIntent.payment_method:', collect.setupIntent?.payment_method)
      console.log('[autodraft] STEP 2 RESULT — full setupIntent:', JSON.stringify(collect.setupIntent, null, 2))
      if (collect.error) throw new Error(collect.error.message || 'Bank link cancelled.')

      let si = collect.setupIntent
      if (si?.status === 'requires_confirmation') {
        console.log('[autodraft] STEP 3 — calling stripe.confirmUsBankAccountSetup(clientSecret) with NO second arg')
        const confirmed = await stripe.confirmUsBankAccountSetup(clientSecret)
        console.log('[autodraft] STEP 3 RESULT — confirmed.error:', confirmed.error)
        console.log('[autodraft] STEP 3 RESULT — confirmed.setupIntent.status:', confirmed.setupIntent?.status)
        console.log('[autodraft] STEP 3 RESULT — full confirmed setupIntent:', JSON.stringify(confirmed.setupIntent, null, 2))
        if (confirmed.error) throw new Error(confirmed.error.message || 'Bank link confirmation failed.')
        si = confirmed.setupIntent
      } else {
        console.log('[autodraft] STEP 3 — SKIPPED (setupIntent already at status:', si?.status, ')')
      }
      if (si?.status !== 'succeeded') {
        // instant-only setup: a bank that can't be instantly verified ends
        // here (never micro-deposit / requires_action). Fail clean — nothing
        // saved, nothing pending — with a clear, non-technical message.
        throw new Error("This bank can't be verified instantly — please use a card or a different bank.")
      }
      const pmId = typeof si.payment_method === 'string'
        ? si.payment_method
        : si.payment_method?.id
      console.log('[autodraft] STEP 4 — extracted payment method id:', pmId)
      if (!pmId) throw new Error('Bank link succeeded but no payment method was returned.')

      console.log('[autodraft] STEP 5 — calling confirmInvoiceAutoDraft on server with:', { invoiceId, paymentMethodId: pmId })
      const r = await confirmInvoiceAutoDraft(invoiceId, pmId)
      console.log('[autodraft] STEP 5 RESULT — server returned:', r)
      if (!r?.ok) throw new Error(r?.error || 'Could not finish auto-draft setup.')
      onSuccess?.()
    } catch (e) {
      console.error('[autodraft] FAILED at one of the steps above. Error:', e)
      console.error('[autodraft] Error message:', e?.message)
      console.error('[autodraft] Error type:', e?.type)
      console.error('[autodraft] Error code:', e?.code)
      console.error('[autodraft] Full error object:', JSON.stringify(e, Object.getOwnPropertyNames(e), 2))
      setError(e?.message || 'Could not complete setup.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="text-center">
      <h1 className="font-extrabold text-2xl">Auto Draft Setup</h1>
      <p className="text-sm text-gray-900 mt-2 leading-relaxed">
        Link your bank account once. We'll draft your YesNet WiFi bill
        automatically each month — nothing to do after setup.
      </p>

      <div className="bg-gray-50 rounded-lg p-4 mt-5 border border-gray-200">
        <div className="text-xs uppercase tracking-widest text-gray-900 font-bold">
          You will NOT be charged today
        </div>
        <p className="text-sm text-gray-900 mt-2 leading-relaxed">
          Invoice <b>#{invoice.invoiceNumber}</b> for <b>{fmtMoney(invoice.amount)}</b>{' '}
          will be charged manually by our team after you finish linking your bank.
          Future months will draft on the due date automatically.
        </p>
      </div>

      {error ? (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg px-3 py-2 mt-4 mb-2 text-left">
          {error}
        </div>
      ) : null}

      <button
        type="button"
        onClick={go}
        disabled={busy}
        className="btn-gold w-full mt-5"
      >
        {busy ? 'Opening secure bank link…' : 'Link Bank for Auto Draft'}
      </button>
      <p className="text-[11px] text-gray-900 mt-3">
        Powered by Stripe — your bank credentials never touch our servers.
      </p>
    </div>
  )
}

// ── card_invoice + card_auto (Card Element) flow ──────────────────────────
// Two states:
//   hasSavedCard (card_invoice only): tap Pay → chargeInvoiceWithSavedCard
//     → off_session charge against the saved card.
//   no saved card (both rails): mount Stripe Card Element, customer enters
//     details, createCardPaymentIntent gives us a clientSecret, then
//     stripe.confirmCardPayment with the card element finishes the charge.
//     setup_future_usage:'off_session' on the PI tells Stripe to attach
//     the PM; webhook saves it back to our customer record + stamps
//     funding so subsequent invoices apply the credit-card surcharge.
function CardInvoicePay({ invoice, invoiceId, onSuccess }) {
  const cardSlotRef = useRef(null)
  const cardElementRef = useRef(null)
  const [stripe, setStripe] = useState(null)
  const [mounted, setMounted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const hasSavedCard = !!invoice.hasSavedCard
  // For repeat one-tap pays the surcharged total comes from getInvoiceForPayment
  // (chargedAmount + surcharge). For first-time payments the PI charges base
  // amount (funding unknown until after charge), so we show the base.
  const displayAmount = hasSavedCard && invoice.surcharge
    ? Number(invoice.chargedAmount) || Number(invoice.amount) || 0
    : Number(invoice.amount) || 0

  useEffect(() => {
    if (hasSavedCard) return // one-tap path doesn't need the element
    let cancelled = false
    ;(async () => {
      const s = await getStripe()
      if (cancelled) return
      if (!s) { setError('Payment system unavailable — please refresh and try again.'); return }
      setStripe(s)
      if (!cardSlotRef.current || cardElementRef.current) return
      const elements = s.elements()
      const card = elements.create('card', {
        style: { base: { fontSize: '16px', color: '#111827', '::placeholder': { color: '#9CA3AF' } } },
      })
      card.mount(cardSlotRef.current)
      cardElementRef.current = card
      setMounted(true)
    })()
    return () => {
      cancelled = true
      if (cardElementRef.current) {
        try { cardElementRef.current.destroy() } catch { /* ignore */ }
        cardElementRef.current = null
      }
    }
  }, [hasSavedCard])

  const go = async () => {
    setError(''); setBusy(true)
    try {
      if (hasSavedCard) {
        const r = await chargeInvoiceWithSavedCard(invoiceId)
        if (!r?.ok) throw new Error(r?.error || 'Could not charge saved card.')
        onSuccess?.()
        return
      }
      if (!stripe) throw new Error('Card form is still loading — please wait a moment.')
      if (!cardElementRef.current) throw new Error('Card form is not ready — please refresh.')

      const { clientSecret } = await createCardPaymentIntent(invoiceId)
      if (!clientSecret) throw new Error('Payment could not start — please try again.')

      // Create the PaymentMethod first so we can read its funding type BEFORE
      // charging. Block prepaid at setup; debit, credit, and unknown proceed
      // (debit is accepted; never block a legitimate credit card).
      const pmResult = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElementRef.current,
        billing_details: { name: invoice.customerName || 'Customer' },
      })
      if (pmResult.error) throw new Error(pmResult.error.message || 'Card error — please check your card details.')
      const funding = pmResult.paymentMethod?.card?.funding
      if (funding === 'prepaid') {
        throw new Error("We're unable to accept prepaid cards. Please use a debit or credit card, or link your bank account instead.")
      }

      const confirmed = await stripe.confirmCardPayment(clientSecret, {
        payment_method: pmResult.paymentMethod.id,
      })
      if (confirmed.error) throw new Error(confirmed.error.message || 'Card payment failed.')
      const pi = confirmed.paymentIntent
      if (pi?.status !== 'succeeded' && pi?.status !== 'processing') {
        throw new Error(`Payment did not complete (state: ${pi?.status || 'unknown'}).`)
      }
      // Persist the card to the customer record (mirrors the bank flow's
      // confirmInvoiceBankInvoice) so next month's invoice shows a Pay Now
      // button. createCardPaymentIntent set setup_future_usage, so Stripe has
      // already attached the card; this just writes it to our record. The
      // charge above already succeeded — a save failure here must NOT block
      // the success UX, so swallow + log.
      const savedPmId = typeof pi?.payment_method === 'string' ? pi.payment_method : pi?.payment_method?.id
      if (savedPmId) {
        try { await confirmInvoiceCardPayment(invoiceId, savedPmId) }
        catch (e) { console.warn('[card] saving card for reuse failed (payment still succeeded):', e?.message) }
      }
      onSuccess?.()
    } catch (e) {
      setError(e?.message || 'Could not complete payment.')
    } finally {
      setBusy(false)
    }
  }

  const isAutoSetup = invoice.paymentMethod === 'card_auto'

  return (
    <div>
      <InvoiceHeader invoice={invoice} />

      {hasSavedCard && invoice.surcharge ? (
        <table className="w-full mt-4 text-sm border-t border-gray-200">
          <tbody>
            <tr className="border-b border-gray-100">
              <td className="py-2 text-gray-900">Base service</td>
              <td className="py-2 text-right font-semibold">{fmtMoney(invoice.amount)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-2 text-gray-900">Card processing fee</td>
              <td className="py-2 text-right font-semibold">{fmtMoney(invoice.surcharge)}</td>
            </tr>
            <tr>
              <td className="py-3 text-gray-900 font-bold">Total</td>
              <td className="py-3 text-right font-extrabold text-brand-green text-lg">{fmtMoney(displayAmount)}</td>
            </tr>
          </tbody>
        </table>
      ) : null}

      <div className="mt-4 text-sm text-gray-900 text-center">
        {hasSavedCard ? (
          <>
            <p className="mb-2">
              Card on file: <b>{invoice.cardBrand && invoice.cardLast4 ? `${invoice.cardBrand[0].toUpperCase()}${invoice.cardBrand.slice(1)} ••${invoice.cardLast4}` : 'saved card'}</b>.
              Tap below to charge this invoice.
            </p>
            <p className="text-xs text-gray-900">
              Payment is immediate — receipt sent on success.
            </p>
          </>
        ) : (
          <>
            <p className="mb-2">
              {isAutoSetup
                ? 'Enter a card to start your auto-pay. We\'ll charge this invoice now and bill the same card on each due date.'
                : 'Enter a card to pay this invoice. We\'ll save it for one-tap pay on next month\'s invoice.'}
            </p>
            <p className="text-xs text-gray-900">
              Credit cards include a ~3.3% processing fee on each invoice; debit cards
              are charged at face value.
            </p>
          </>
        )}
      </div>

      {!hasSavedCard ? (
        <div className="mt-4">
          <label className="label">Card details</label>
          <div
            ref={cardSlotRef}
            className="border border-gray-300 rounded-md p-3 bg-white"
            style={{ minHeight: 44 }}
          />
          <p className="text-xs text-gray-500 mt-1.5">
            💳 Credit cards only — debit &amp; prepaid cards aren't accepted. To avoid card fees, link your bank account instead.
          </p>
        </div>
      ) : null}

      {error ? (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg px-3 py-2 mt-3 text-left">
          {error}
        </div>
      ) : null}

      <button
        type="button"
        onClick={go}
        disabled={busy || (!hasSavedCard && !mounted)}
        className="btn-gold w-full mt-4"
      >
        {busy
          ? (hasSavedCard ? 'Charging card…' : 'Processing payment…')
          : hasSavedCard
            ? `Pay ${fmtMoney(displayAmount)}`
            : (isAutoSetup ? 'Save Card & Pay' : 'Pay with Card')}
      </button>
      <p className="text-[11px] text-gray-900 text-center mt-3">
        Powered by Stripe — your card details never touch our servers.
      </p>
    </div>
  )
}

// ── form ───────────────────────────────────────────────────────────────────

function PayForm({ invoice, invoiceId, onSuccess }) {
  const [holder, setHolder] = useState('')
  const [routing, setRouting] = useState('')
  const [account, setAccount] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const onlyDigits = (s) => s.replace(/\D/g, '')

  const localError = (() => {
    if (!holder.trim()) return ''
    if (routing.length === 9 && !/^\d{9}$/.test(routing)) return 'Routing number must be 9 digits.'
    if (confirm.length > 0 && confirm !== account) return 'Account numbers don\'t match.'
    return ''
  })()

  const canSubmit = !busy
    && holder.trim().length > 0
    && /^\d{9}$/.test(routing)
    && /^\d{4,17}$/.test(account)
    && confirm === account

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!canSubmit) return
    setError(''); setBusy(true)
    try {
      const result = await submitInvoiceAchPayment({
        invoiceId,
        accountHolderName: holder.trim(),
        routingNumber: routing,
        accountNumber: account,
        accountType: 'checking',
      })
      if (!result?.ok) throw new Error(result?.error || 'Could not process payment.')
      onSuccess(result.status)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[pay] failed:', e)
      setError(e?.message || 'Could not process payment.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <InvoiceHeader invoice={invoice} />

      <form onSubmit={onSubmit} className="mt-5 space-y-3">
        <Field label="Account holder name">
          <input
            className="input"
            autoComplete="cc-name"
            autoCapitalize="words"
            placeholder={invoice.customerName || 'Full name on the bank account'}
            value={holder}
            onChange={(e) => setHolder(e.target.value)}
          />
        </Field>
        <Field label="Routing number (9 digits)">
          <input
            className="input"
            inputMode="numeric"
            autoComplete="off"
            maxLength={9}
            value={routing}
            onChange={(e) => setRouting(onlyDigits(e.target.value).slice(0, 9))}
            placeholder="123456789"
          />
        </Field>
        <Field label="Bank account number">
          <input
            className="input"
            inputMode="numeric"
            autoComplete="off"
            maxLength={17}
            value={account}
            onChange={(e) => setAccount(onlyDigits(e.target.value).slice(0, 17))}
          />
        </Field>
        <Field label="Confirm bank account number">
          <input
            className="input"
            inputMode="numeric"
            autoComplete="off"
            maxLength={17}
            value={confirm}
            onChange={(e) => setConfirm(onlyDigits(e.target.value).slice(0, 17))}
          />
        </Field>

        {(error || localError) ? (
          <p className="text-sm font-bold text-red-600">{error || localError}</p>
        ) : null}

        <button
          type="submit"
          disabled={!canSubmit}
          className="btn-gold w-full mt-1">
          {busy ? 'Submitting…' : 'Submit Payment'}
        </button>
        <p className="text-[11px] text-gray-900 text-center mt-2">
          Bank transfers take 1–3 business days to clear. You'll receive a
          receipt by email once it processes.
        </p>
      </form>
    </div>
  )
}

function InvoiceHeader({ invoice }) {
  return (
    <>
      <h1 className="font-extrabold text-2xl text-center mb-1">Pay your invoice</h1>
      <p className="text-center text-sm text-gray-900 mb-4">
        {invoice.customerName ? `For ${invoice.customerName}` : ''}
      </p>
      <div className="text-center">
        <div className="text-[11px] uppercase tracking-widest text-gray-900 font-bold">Amount Due</div>
        <div className="text-4xl font-extrabold text-brand-green mt-1">{fmtMoney(invoice.amount)}</div>
        <div className="text-sm text-gray-900 mt-2">Due <b>{invoice.dueDateLong || invoice.dueDate}</b></div>
        <div className="text-xs text-gray-900 mt-1">Invoice #{invoice.invoiceNumber}</div>
      </div>
    </>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
    </div>
  )
}

// ── presentation ───────────────────────────────────────────────────────────

// Outer + inner card mirror the invoice email: light blue-gray page
// background (#f4f7fc), single white card (560px max) with subtle shadow
// and a 4px navy stripe at top — same system font stack so the customer
// experiences a single continuous visual from email through payment
// setup. NO full-screen navy gradient.
function Shell({ children }) {
  return (
    <div
      className="min-h-screen flex items-start justify-center p-4 sm:p-8"
      style={{
        backgroundColor: '#f4f7fc',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        color: '#111827',
      }}>
      <div className="w-full max-w-[560px]">
        <div
          className="bg-white overflow-hidden"
          style={{ borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <div className="text-center" style={{ padding: '32px 24px 18px', background: '#ffffff' }}>
            <img src={logo} alt="YesNet" className="block mx-auto max-h-24 object-contain" />
          </div>
          <div style={{ height: 4, backgroundColor: '#1a3a8f' }} />
          <div style={{ padding: '24px' }}>
            {children}
          </div>
        </div>
        <p className="text-center text-xs mt-4" style={{ color: '#6b7280' }}>
          Questions? <a href="mailto:yesnetwifi@gmail.com" style={{ color: '#1a3a8f', fontWeight: 700 }}>yesnetwifi@gmail.com</a>
        </p>
      </div>
    </div>
  )
}

function Loading() {
  return (
    <div className="text-center">
      <p className="text-sm text-gray-900">Loading invoice…</p>
    </div>
  )
}

function NotFound() {
  return (
    <div className="text-center">
      <div className="text-4xl mb-2">⚠️</div>
      <h2 className="font-extrabold text-lg">Invoice not found</h2>
      <p className="text-sm text-gray-900 mt-1">
        We couldn't find an invoice with that link. Reply to the email you received and we'll help sort it out.
      </p>
    </div>
  )
}

function AlreadyPaid({ invoice }) {
  return (
    <div className="text-center">
      <div className="text-5xl mb-2">✅</div>
      <h2 className="font-extrabold text-xl">Already paid</h2>
      <p className="text-sm text-gray-900 mt-2">
        Invoice <b>#{invoice.invoiceNumber}</b> for <b>{fmtMoney(invoice.amount)}</b> has been paid in full. Nothing more to do.
      </p>
    </div>
  )
}

function AutoDraftNotice({ invoice }) {
  // Card-credit customers see a 3-line breakdown (base, surcharge, total)
  // matching the email. Bank, debit-card, and legacy customers see the
  // single auto-draft amount. The server already pre-computed
  // chargedAmount + surcharge per invoice — we just render them.
  const isCardCredit = invoice.paymentMethodType === 'card' && invoice.cardFunding === 'credit'
  const cardLabel = invoice.cardBrand && invoice.cardLast4
    ? `${invoice.cardBrand[0].toUpperCase()}${invoice.cardBrand.slice(1)} ••${invoice.cardLast4}`
    : 'card on file'
  const methodLabel = invoice.paymentMethodType === 'card' ? cardLabel : 'bank on file'
  return (
    <div>
      <div className="text-5xl mb-2 text-center">🏦</div>
      <h2 className="font-extrabold text-xl text-center">Auto-pay is set up</h2>

      {isCardCredit ? (
        <>
          <p className="text-sm text-gray-900 mt-3 text-center">
            For invoice <b>#{invoice.invoiceNumber}</b>, your {methodLabel} will be charged on <b>{invoice.dueDateLong || invoice.dueDate}</b>:
          </p>
          <table className="w-full mt-4 text-sm border-t border-gray-200">
            <tbody>
              <tr className="border-b border-gray-100">
                <td className="py-2 text-gray-900">Base service</td>
                <td className="py-2 text-right font-semibold">{fmtMoney(invoice.amount)}</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 text-gray-900">Card processing fee</td>
                <td className="py-2 text-right font-semibold">{fmtMoney(invoice.surcharge)}</td>
              </tr>
              <tr>
                <td className="py-3 text-gray-900 font-bold">Total</td>
                <td className="py-3 text-right font-extrabold text-brand-green text-lg">{fmtMoney(invoice.chargedAmount)}</td>
              </tr>
            </tbody>
          </table>
        </>
      ) : (
        <p className="text-sm text-gray-900 mt-3 text-center">
          Your payment of <b className="text-brand-green">{fmtMoney(invoice.amount)}</b> for invoice <b>#{invoice.invoiceNumber}</b> will be auto-drafted from your {methodLabel} on <b>{invoice.dueDateLong || invoice.dueDate}</b>.
        </p>
      )}

      <p className="text-xs text-gray-900 mt-4 text-center">
        No action needed. We'll send a receipt once the charge clears.
      </p>
    </div>
  )
}

// Shown the moment a payment is in flight after the customer submits
// via /pay. Customer-facing wording, no Pay button (avoids double-
// charge), amount rendered live from the invoice so they see exactly
// what was charged.
function PaymentReceived({ invoice, rail }) {
  // Three success variants, one per chooser option. The `rail` prop is set
  // by PayPage's onSuccess callback for each sub-component. Defaults to
  // the bank-invoice copy for legacy callers (returning customers, in-
  // flight payments) that don't pass a rail.
  if (rail === 'auto-draft') {
    return (
      <div className="text-center">
        <div className="text-5xl mb-2">🎉</div>
        <h2 className="font-extrabold text-xl">You're all set!</h2>
        <p className="text-sm text-gray-900 mt-3 leading-relaxed">
          Bank linked. Your first payment will be drafted automatically.
          You have <b>NOT</b> been charged today.
        </p>
        <p className="text-xs text-gray-900 mt-5 leading-relaxed">
          Questions? Call Mark Ratliff at{' '}
          <a href="tel:+18704894087" className="font-bold text-brand-green">870-489-4087</a>.
        </p>
      </div>
    )
  }
  if (rail === 'mail-set') {
    return (
      <div className="text-center">
        <div className="text-5xl mb-2">✉️</div>
        <h2 className="font-extrabold text-xl">You're set up for mailing payment.</h2>
        <p className="text-sm text-gray-900 mt-3 leading-relaxed">
          Send your check to YesNet WiFi by your due date each month.
        </p>
        <p className="text-xs text-gray-900 mt-5 leading-relaxed">
          Questions? Call Mark Ratliff at{' '}
          <a href="tel:+18704894087" className="font-bold text-brand-green">870-489-4087</a>.
        </p>
      </div>
    )
  }
  if (rail === 'bank-invoice-linked') {
    return (
      <div className="text-center">
        <div className="text-5xl mb-2">🎉</div>
        <h2 className="font-extrabold text-xl">Bank linked!</h2>
        <p className="text-sm text-gray-900 mt-3 leading-relaxed">
          Your bank is saved on file. You have <b>NOT</b> been charged today.
        </p>
        <p className="text-sm text-gray-900 mt-3 leading-relaxed">
          You'll receive an invoice each month — just click Pay when it arrives.
        </p>
        <p className="text-xs text-gray-900 mt-5 leading-relaxed">
          Questions? Call Mark Ratliff at{' '}
          <a href="tel:+18704894087" className="font-bold text-brand-green">870-489-4087</a>.
        </p>
      </div>
    )
  }
  if (rail === 'card-invoice') {
    return (
      <div className="text-center">
        <div className="text-5xl mb-2">🎉</div>
        <h2 className="font-extrabold text-xl">Payment submitted</h2>
        <p className="text-sm text-gray-900 mt-3 leading-relaxed">
          Your <b className="text-brand-green">{fmtMoney(invoice.amount)}</b> payment
          for invoice <b>#{invoice.invoiceNumber}</b> is confirmed. Your card is
          saved for future invoices.
        </p>
        <p className="text-sm text-gray-900 mt-3">
          Click Pay when each invoice arrives.
        </p>
        <p className="text-xs text-gray-900 mt-5 leading-relaxed">
          Questions? Call Mark Ratliff at{' '}
          <a href="tel:+18704894087" className="font-bold text-brand-green">870-489-4087</a>.
        </p>
      </div>
    )
  }
  // Default = bank-invoice (also the fallback for existing customers
  // returning to pay a repeat invoice).
  return (
    <div className="text-center">
      <div className="text-5xl mb-2">🎉</div>
      <h2 className="font-extrabold text-xl">Payment submitted</h2>
      <p className="text-sm text-gray-900 mt-3 leading-relaxed">
        Your <b className="text-brand-green">{fmtMoney(invoice.amount)}</b> payment
        for invoice <b>#{invoice.invoiceNumber}</b> is confirmed. Your bank is
        saved for future invoices.
      </p>
      <p className="text-sm text-gray-900 mt-3">
        Your bank transfer should clear within <b>3–5 business days</b>.
        Click Pay when each invoice arrives.
      </p>
      <p className="text-xs text-gray-900 mt-5 leading-relaxed">
        Questions? Call Mark Ratliff at{' '}
        <a href="tel:+18704894087" className="font-bold text-brand-green">870-489-4087</a>.
      </p>
    </div>
  )
}

function fmtMoney(n) {
  const v = Number(n) || 0
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}
